import React, { useState, useEffect, useRef } from 'react';
import {
  LocalAudioTrack,
  LocalParticipant as TwilioLocalParticipant,
  LocalVideoTrack,
  LocalTrackPublication,
} from 'twilio-video';
import InitialsAvatar from 'components/messaging/components/initials-avatar';

interface LocalParticipantProps {
  participant: TwilioLocalParticipant;
  videoTrackEnable: boolean;
  audioTrackEnable: boolean;
}

const LocalParticipant: React.FC<LocalParticipantProps> = ({
  participant,
  videoTrackEnable,
  audioTrackEnable,
}) => {
  const [videoTracks, setVideoTracks] = useState<LocalVideoTrack[]>([]);
  const [audioTracks, setAudioTracks] = useState<LocalAudioTrack[]>([]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const trackpubsToTracks = (trackMap: any) =>
    Array.from(trackMap.values())
      .map((publication: any) => publication.track)
      .filter((track: any) => track !== null);

  useEffect(() => {
    const syncTracks = () => {
      setVideoTracks(trackpubsToTracks(participant.videoTracks));
      setAudioTracks(trackpubsToTracks(participant.audioTracks));
    };

    syncTracks();

    const trackPublished = (_publication: LocalTrackPublication) => {
      syncTracks();
    };

    const trackUnpublished = (_publication: LocalTrackPublication) => {
      syncTracks();
    };

    participant.on('trackPublished', trackPublished);
    participant.on('trackUnpublished', trackUnpublished);

    return () => {
      participant.off('trackPublished', trackPublished);
      participant.off('trackUnpublished', trackUnpublished);
      setVideoTracks([]);
      setAudioTracks([]);
    };
  }, [participant]);

  useEffect(() => {
    const primaryVideoTrack = videoTracks[videoTracks.length - 1];
    if (videoRef.current) {
      // Detach all tracks first
      videoTracks.forEach((track) => {
        try {
          track.detach();
        } catch (e) {
          // Already detached
        }
      });

      // Attach only the primary (latest) track
      if (primaryVideoTrack) {
        if (videoTrackEnable) {
          primaryVideoTrack.enable();
        } else {
          primaryVideoTrack.disable();
        }
        primaryVideoTrack.attach(videoRef.current);
      }
    }

    return () => {
      if (videoRef.current) {
        videoTracks.forEach((track) => {
          try {
            track.detach();
          } catch (e) {
            // Already detached
          }
        });
      }
    };
  }, [videoTracks, videoTrackEnable]);

  useEffect(() => {
    const primaryAudioTrack = audioTracks[audioTracks.length - 1];
    if (audioRef.current) {
      // Detach all tracks first
      audioTracks.forEach((track) => {
        try {
          track.detach();
        } catch (e) {
          // Already detached
        }
      });

      // Attach only the primary (latest) track
      if (primaryAudioTrack) {
        if (audioTrackEnable) {
          primaryAudioTrack.enable();
        } else {
          primaryAudioTrack.disable();
        }
        primaryAudioTrack.attach(audioRef.current);
      }
    }

    return () => {
      if (audioRef.current) {
        audioTracks.forEach((track) => {
          try {
            track.detach();
          } catch (e) {
            // Already detached
          }
        });
      }
    };
  }, [audioTracks, audioTrackEnable]);

  const userInfo = JSON.parse(participant.identity);

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 10,
        right: 10,
        width: 150,
        height: 150,
        border: '2px solid white',
        borderRadius: 8,
        overflow: 'hidden',
        zIndex: 10,
        background: '#000',
      }}
    >
      {videoTracks.length > 0 ? (
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <InitialsAvatar
          name={userInfo.username?.replace(/[0-9]/g, '')}
          maxInitials={2}
          textSizeRatio={1.75}
          size="150"
          round
        />
      )}
      <audio ref={audioRef} autoPlay />
    </div>
  );
};

export default LocalParticipant;